import {CandidateSummary} from './blocks/CandidateSummary';
import {
    Matches,
    Skills,
    CandidateDetails,
    Technologies,
    Frameworks,
    Description
} from "@/pages/buddy/candidate/blocks";
import {CandidateDetailsData} from "@/pages/buddy/types/CandidateDetailsData.ts";
import {JobHistory} from "@/pages/buddy/candidate/blocks/JobHistory.tsx";
import {Education} from "@/pages/buddy/candidate/blocks/Education.tsx";
import {Files} from "@/components/file/Files.tsx";
import {Certifications} from "@/pages/buddy/candidate/blocks/Certifications.tsx";
import {SpokenLanguages} from "@/pages/buddy/candidate/blocks/SpokenLangauges.tsx";
import {LLMLog} from "@/pages/buddy/shared/debug/llm_log.tsx";
import React, {useMemo} from "react";
import EditableList from "@/components/editable-list/EditableList.tsx";
import {ChatConfig, SidebarChat} from "@/layouts/buddy";

interface CandidateContentProps {
    activePage: string;
    candidate: CandidateDetailsData;
    loading: boolean;
    setActivePage: (page: string) => void;
    handleSummaryFetched: (activeJobs: number) => void;
    loadingJobHistory: boolean;
    loadingMatches: boolean;
    onCpsUpdateComplete: () => void;
}

const CandidateContent = ({
                              activePage,
                              candidate,
                              loading,
                              setActivePage,
                              handleSummaryFetched,
                              loadingJobHistory,
                              loadingMatches,
                              onCpsUpdateComplete
                          }: CandidateContentProps) => {

    const chatConfig: ChatConfig = useMemo(() => ({
        entityType: 'candidate',
        entityId: candidate.p_id,
        title: 'Refine This Job Profile',
        instructions: 'Give Buddy your instructions to tailor this candidate profile to your needs - quickly, clearly, and effortlessly.'
    }), [candidate.p_id]);

    const renderOverview = () => (
        <>
            {/* ✅ Pass refresh handler to SidebarChat */}
            <SidebarChat config={chatConfig} onUpdateComplete={onCpsUpdateComplete}/>

            <CandidateSummary
                setActivePage={setActivePage}
                onSummaryFetched={handleSummaryFetched}
                loadingMatches={loadingMatches}
            />
            <Description title="Description" description={candidate.description} candidate_p_id={candidate.p_id}/>
            <div className="grid lg:grid-cols-3 gap-5 lg:gap-7.5">
                <div className="lg:col-span-1">

                    <div className="mb-7.5">
                        <EditableList
                            title="Skills"
                            items={candidate.skills}
                            entity={"candidate"}
                            entityId={candidate.p_id}
                            endpoint="skills"
                        />
                    </div>

                    <div className="mb-7.5">
                        <EditableList
                            title="Frameworks"
                            items={candidate.frameworks}
                            entity={"candidate"}
                            entityId={candidate.p_id}
                            endpoint="frameworks"
                        />
                    </div>

                    <div className="mb-7.5">
                        <EditableList
                            title="Technologies"
                            items={candidate.technologies}
                            entity={"candidate"}
                            entityId={candidate.p_id}
                            endpoint="technologies"
                        />
                    </div>
                    <div className="mb-7.5">
                        <Certifications title="Certifications" items={candidate.certifications}
                                        candidate_p_id={candidate.p_id}/>
                    </div>
                    <div className="mb-7.5">
                        <EditableList
                            title="Spoken Languages"
                            items={candidate.spoken_languages}
                            entity={"candidate"}
                            entityId={candidate.p_id}
                            endpoint="spoken-languages"
                        />
                    </div>
                    <div className="mb-7.5">
                        <Files title="Sources" items={candidate.files}/>
                    </div>
                </div>
                <div className="lg:col-span-2">
                    <div className="mb-7.5">
                        <CandidateDetails title="Details" candidate={candidate} candidate_p_id={candidate.p_id}/>
                    </div>
                    <div className="mb-7.5">
                        <JobHistory
                            title="Job History"
                            items={candidate.job_history}
                            candidate_p_id={candidate.p_id}
                            loadingJobHistory={loadingJobHistory}
                        />
                    </div>
                    <div className="mb-7.5">
                        <Education title="Education" items={candidate.education} candidate_p_id={candidate.p_id}/>
                    </div>
                </div>
            </div>

            <LLMLog
                title="LLM Logs Base (Dev/Testing Only)"
                llmLogs={candidate.llm_log || []}
            />

            <LLMLog
                title="LLM Logs Job History (Dev/Testing Only)"
                llmLogs={candidate.job_history_llm_log || []}
            />
        </>
    );

    const renderMatches = () => (
        <Matches/>
    );

    return (
        <div className="grid grid-cols-1 gap-5 lg:gap-7.5">
            {activePage === 'overview' ? renderOverview() : renderMatches()}
        </div>
    );
};

export {CandidateContent};
